import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { withNamespaces } from "react-i18next";
import { Alert, Button, Input, InputGroup, InputGroupText } from "reactstrap";
import { applyTextHighlights, setActiveHighlight } from "./highlight";
import "./style.scss";

const SEARCH_DEBOUNCE_MS = 150;

const assignRef = (ref, value) => {
	if (!ref) {
		return;
	}
	if (typeof ref === "function") {
		ref(value);
	} else {
		ref.current = value;
	}
};

const UserManualSearch = ({
	content,
	contentClassName = "",
	contentRef,
	placeholder,
	t,
}) => {
	const [query, setQuery] = useState("");
	const [debouncedQuery, setDebouncedQuery] = useState("");
	const [matchCount, setMatchCount] = useState(0);
	const [activeIndex, setActiveIndex] = useState(-1);

	const contentNodeRef = useRef(null);
	const matchElsRef = useRef([]);
	const inputRef = useRef(null);

	useEffect(() => {
		// Clear immediately when the input is emptied; debounce only while typing.
		if (!query.trim()) {
			setDebouncedQuery("");
			return undefined;
		}

		const timeoutId = setTimeout(() => {
			setDebouncedQuery(query);
		}, SEARCH_DEBOUNCE_MS);

		return () => clearTimeout(timeoutId);
	}, [query]);

	const setContentNode = useCallback(
		(node) => {
			contentNodeRef.current = node;
			assignRef(contentRef, node);
		},
		[contentRef]
	);

	const clearSearch = useCallback(() => {
		setQuery("");
		setDebouncedQuery("");
		setMatchCount(0);
		setActiveIndex(-1);
		matchElsRef.current = [];
		if (contentNodeRef.current) {
			contentNodeRef.current.innerHTML = content || "";
		}
		inputRef.current?.focus();
	}, [content]);

	useLayoutEffect(() => {
		const container = contentNodeRef.current;
		if (!container) {
			return;
		}

		// Always restore the original HTML from the prop (never mutate the source string).
		container.innerHTML = content || "";

		const trimmedQuery = debouncedQuery.trim();
		if (!trimmedQuery) {
			matchElsRef.current = [];
			setMatchCount(0);
			setActiveIndex(-1);
			return;
		}

		const marks = applyTextHighlights(container, trimmedQuery);
		matchElsRef.current = marks;
		setMatchCount(marks.length);
		setActiveIndex(marks.length ? 0 : -1);
		setActiveHighlight(marks, marks.length ? 0 : -1);
	}, [content, debouncedQuery]);

	useLayoutEffect(() => {
		const marks = matchElsRef.current;
		if (!marks.length || activeIndex < 0) {
			return;
		}

		setActiveHighlight(marks, activeIndex);
		marks[activeIndex]?.scrollIntoView({
			behavior: "smooth",
			block: "center",
			inline: "nearest",
		});
	}, [activeIndex, matchCount]);

	const goToResult = useCallback(
		(direction) => {
			if (!matchCount) {
				return;
			}

			setActiveIndex((current) => {
				if (current < 0) {
					return 0;
				}
				return (current + direction + matchCount) % matchCount;
			});
		},
		[matchCount]
	);

	const handleKeyDown = (event) => {
		if (event.key === "Escape") {
			if (query) {
				event.preventDefault();
				clearSearch();
			}
			return;
		}

		if (event.key === "Enter") {
			if (!matchCount) {
				return;
			}
			event.preventDefault();
			goToResult(event.shiftKey ? -1 : 1);
		}
	};

	const trimmedDebouncedQuery = debouncedQuery.trim();
	const hasResults = matchCount > 0;
	const showNoResults = Boolean(trimmedDebouncedQuery) && !hasResults;
	const inputPlaceholder = placeholder || t("Search in User Manual...");

	return (
		<div className="user-manual-search">
			<div className="user-manual-search__bar">
				<div className="user-manual-search__panel">
					<InputGroup className="user-manual-search__input-group">
						<InputGroupText>
							<i className="ri-search-line" aria-hidden="true" />
						</InputGroupText>
						<Input
							innerRef={inputRef}
							type="text"
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							onKeyDown={handleKeyDown}
							placeholder={inputPlaceholder}
							aria-label={inputPlaceholder}
							autoComplete="off"
							spellCheck={false}
						/>
						{query ? (
							<Button
								color="link"
								onClick={clearSearch}
								aria-label={t("Clear search")}
								title={t("Clear search")}
							>
								<i className="ri-close-line" aria-hidden="true" />
							</Button>
						) : null}
					</InputGroup>

					{hasResults ? (
						<div
							className="user-manual-search__controls"
							role="status"
							aria-live="polite"
						>
							<span className="user-manual-search__count">
								<strong>{activeIndex + 1}</strong> / {matchCount}
							</span>
							<Button
								color="light"
								className="user-manual-search__nav-btn"
								onClick={() => goToResult(-1)}
								aria-label={t("Previous result")}
								title={t("Previous result")}
							>
								<i className="ri-arrow-up-s-line" aria-hidden="true" />
							</Button>
							<Button
								color="light"
								className="user-manual-search__nav-btn"
								onClick={() => goToResult(1)}
								aria-label={t("Next result")}
								title={t("Next result")}
							>
								<i className="ri-arrow-down-s-line" aria-hidden="true" />
							</Button>
						</div>
					) : null}
				</div>
			</div>

			{showNoResults ? (
				<Alert color="light" className="user-manual-search__no-results">
					<i className="ri-information-line" aria-hidden="true" />
					<span>
						<span className="user-manual-search__no-results-title">
							{t('No results found for "{{query}}"', {
								query: trimmedDebouncedQuery,
							})}
						</span>
						<span className="user-manual-search__no-results-hint">
							{t("Try another keyword or check the spelling.")}
						</span>
					</span>
				</Alert>
			) : null}

			<div
				ref={setContentNode}
				className={`user-manual-search__content ${contentClassName}`.trim()}
			/>
		</div>
	);
};

export default withNamespaces()(UserManualSearch);
